import { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import FormattedInput from '../../../components/FormattedInput';
import { Phone, Mail, AlertCircle, CheckCircle2 } from 'lucide-react';
import { colaboradoresService } from '../../../services/colaboradores.service';

interface EditarContatoModalProps {
  isOpen: boolean;
  onClose: () => void;
  colaborador: {
    id: string | number;
    nome: string;
    email?: string | null;
    telefone?: string | null;
    is_existente?: boolean;
  } | null;
  onSuccess: (updated: { email?: string; telefone?: string }) => void;
}

export default function EditarContatoModal({
  isOpen,
  onClose,
  colaborador,
  onSuccess,
}: EditarContatoModalProps) {
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (colaborador) {
      setEmail(colaborador.email || '');
      setTelefone(colaborador.telefone ? String(colaborador.telefone).replace(/\D/g, '') : '');
      setError(null);
    }
  }, [colaborador, isOpen]);

  if (!colaborador) return null;

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const cleanEmail = email.trim();
    const cleanPhone = telefone.replace(/\D/g, '').trim();

    if (!cleanEmail && !cleanPhone) {
      setError('Por favor, informe ao menos um canal de contato (Telefone com WhatsApp ou E-mail).');
      return;
    }

    if (cleanEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      setError('Por favor, insira um endereço de e-mail válido.');
      return;
    }

    if (cleanPhone && (cleanPhone.length < 10 || cleanPhone.length > 11)) {
      setError('O telefone deve conter DDD e número válido (10 ou 11 dígitos).');
      return;
    }

    setSaving(true);
    try {
      const colIdNum = parseInt(String(colaborador.id), 10);
      const isPersisted = !isNaN(colIdNum) && !String(colaborador.id).startsWith('local_') && !String(colaborador.id).startsWith('batch_');

      if (isPersisted) {
        await colaboradoresService.atualizarContato(colIdNum, {
          email: cleanEmail || null,
          telefone: cleanPhone || null,
        });
      }

      onSuccess({
        email: cleanEmail || undefined,
        telefone: cleanPhone || undefined,
      });

      onClose();
    } catch (err: unknown) {
      console.error('Erro ao atualizar contato do colaborador:', err);
      const responseData = (err as { response?: { data?: { message?: string | string[] } } })?.response?.data;
      const msg = Array.isArray(responseData?.message)
        ? responseData?.message.join(', ')
        : responseData?.message || 'Erro ao atualizar dados de contato. Tente novamente.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Dados de Contato"
      size="md"
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-colors disabled:opacity-50 cursor-pointer"
          >
            {saving ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Salvando...</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                <span>Salvar Contato</span>
              </span>
            )}
          </button>
        </div>
      }
    >
      <form onSubmit={handleSave} className="space-y-4">
        {/* Identificação do Colaborador */}
        <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider block">
            Colaborador
          </span>
          <p className="text-sm font-bold text-slate-900 mt-0.5">{colaborador.nome}</p>
        </div>

        {/* Alerta explicativo */}
        <div className="bg-blue-50/80 border border-blue-200 rounded-xl p-3 flex items-start gap-2.5">
          <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-xs text-blue-900 leading-relaxed">
            O <strong>WhatsApp</strong> e o <strong>E-mail</strong> são os canais pelos quais o colaborador recebe as orientações e o link para escolher a clínica credenciada.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 font-medium">
            {error}
          </div>
        )}

        {/* Campo Telefone / WhatsApp */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1 flex items-center gap-1.5">
            <Phone className="w-3.5 h-3.5 text-emerald-600" />
            <span>Telefone Celular / WhatsApp</span>
          </label>
          <FormattedInput
            type="phone"
            value={telefone}
            onChange={(val) => setTelefone(val)}
            placeholder="(00) 00000-0000"
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white font-mono text-gray-900 shadow-2xs"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Informe com DDD. O convite será enviado oficialmente via WhatsApp AiDesk.
          </p>
        </div>

        {/* Campo E-mail */}
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1 flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5 text-emerald-600" />
            <span>E-mail do Colaborador</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colaborador@empresa.com.br"
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white text-gray-900 shadow-2xs"
          />
          <p className="text-[11px] text-gray-500 mt-1">
            Receberá também as orientações formais e link de agendamento por e-mail.
          </p>
        </div>
      </form>
    </Modal>
  );
}
